// Raw controls: every device writes named controls into one table, and action maps read only that
// table. Control names (strings, so action maps are plain data an agent can edit):
//
//   keyboard   KeyboardEvent.code: "KeyW", "Space", "ShiftLeft", "ArrowUp"
//   mouse      "Mouse0" (left), "Mouse1" (middle), "Mouse2" (right)
//   pointer    deltas "PointerDrag" (moved while a button is down), "PointerMove", "Wheel"
//   gamepad    buttons "Pad0".."Pad16" (W3C standard mapping), half-axes "PadAxis0+" / "PadAxis0-",
//              sticks (vectors) "PadLeft", "PadRight"
//   touch/any  "Touch:<name>" buttons, vectors and deltas, set by an on-screen layer through the same calls
//
// Edges are latched: a press that starts and ends between two sim steps still counts in the next step,
// and a frame that runs no step never loses a press (feel §16 pitfall).
//
// Device tracking: every device write (not injected input) notes its family, so `device` is the family used last
// and `onDevice` listeners hear a switch synchronously, in the event that caused it (prompts follow within a frame).
// `onPress` listeners hear every device press edge as it happens (frame-level UI actions, rebinding capture).
import { controlInfo, type DeviceFamily } from "./contract";

export interface Vec2 {
  x: number;
  y: number;
}

/** See `Controls.snapshot`. */
export interface ControlsState {
  holds: [string, number][];
  pending: string[];
}

/** Radial dead zone, rescaled so the stick still reaches 1 (feel §11: 0.18 for gamepads). */
export const PAD_DEAD_ZONE = 0.18;

export function radialDeadZone(x: number, y: number, dead = PAD_DEAD_ZONE): Vec2 {
  const m = Math.hypot(x, y);
  if (m <= dead) return { x: 0, y: 0 };
  const k = Math.min(1, (m - dead) / (1 - dead)) / m;
  return { x: x * k, y: y * k };
}

/** The minimum part of a navigator Gamepad this reads (so tests can pass plain objects). */
export interface PadLike {
  connected?: boolean;
  buttons: readonly { value: number; pressed?: boolean }[];
  axes: readonly number[];
}

export class Controls {
  private values = new Map<string, number>();
  private vectors = new Map<string, Vec2>();
  private deltas = new Map<string, Vec2>();
  /** Presses since the last step started. */
  private pending = new Set<string>();
  /** Presses visible to the step running now. */
  private stepEdges = new Set<string>();
  /** Injected holds: control → sim time the hold ends. */
  private holds = new Map<string, number>();
  private padPrev = new Map<string, number>();
  private padVec = { PadLeft: 0, PadRight: 0 };
  private readonly now: () => number;
  private lastDevice: DeviceFamily | null = null;
  private deviceFns = new Set<(d: DeviceFamily) => void>();
  private pressFns = new Set<(control: string) => void>();

  /** `now` is sim time in seconds (injected holds run on it). */
  constructor(now: () => number = () => 0) {
    this.now = now;
  }

  // ── device writes ────────────────────────────────────────────────────────────────────────────────

  press(control: string, value = 1): void {
    const edge = !(this.values.get(control)! > 0.5) && value > 0.5;
    this.values.set(control, value);
    if (edge) this.edge(control);
  }

  release(control: string): void {
    this.values.delete(control);
  }

  /** Release every held control (window blur, page hidden). Injected holds stay: they run on sim time. */
  releaseAll(): void {
    this.values.clear();
    this.vectors.clear();
  }

  setVector(control: string, x: number, y: number): void {
    const was = this.vectors.get(control);
    if (x === 0 && y === 0) this.vectors.delete(control);
    else {
      this.vectors.set(control, { x, y });
      if (!was) this.noteControl(control);
    }
  }

  addDelta(control: string, dx: number, dy: number): void {
    if (dx !== 0 || dy !== 0) this.noteControl(control);
    const d = this.deltas.get(control);
    if (d) {
      d.x += dx;
      d.y += dy;
    } else this.deltas.set(control, { x: dx, y: dy });
  }

  // ── injected input (tests, bots), on sim time ────────────────────────────────────────────────────

  /** Hold a control for `seconds` of sim time from now; counts as a press too. */
  hold(control: string, seconds: number): void {
    this.holds.set(control, this.now() + seconds);
    this.pending.add(control);
  }

  /** A press edge with no hold. */
  tap(control: string): void {
    this.pending.add(control);
  }

  // ── device tracking ──────────────────────────────────────────────────────────────────────────────

  /** The device family used last (null before any device input). */
  get device(): DeviceFamily | null {
    return this.lastDevice;
  }

  /** Note a family as used (a device layer that writes no control, e.g. a touch on a non-control area). */
  noteDevice(family: DeviceFamily): void {
    if (family === this.lastDevice) return;
    this.lastDevice = family;
    for (const f of this.deviceFns) f(family);
  }

  /** Hear every switch of the device used last, synchronously. Returns the remover. */
  onDevice(fn: (family: DeviceFamily) => void): () => void {
    this.deviceFns.add(fn);
    return () => this.deviceFns.delete(fn);
  }

  /** Hear every device press edge (not injected input) as it happens. Returns the remover. */
  onPress(fn: (control: string) => void): () => void {
    this.pressFns.add(fn);
    return () => this.pressFns.delete(fn);
  }

  private noteControl(control: string): void {
    const info = controlInfo(control);
    if (info) this.noteDevice(info.family);
  }

  private edge(control: string): void {
    this.pending.add(control);
    this.noteControl(control);
    for (const f of this.pressFns) f(control);
  }

  // ── reads ────────────────────────────────────────────────────────────────────────────────────────

  value(control: string): number {
    const until = this.holds.get(control);
    if (until !== undefined) {
      if (this.now() < until) return 1;
      this.holds.delete(control);
    }
    return this.values.get(control) ?? 0;
  }

  down(control: string): boolean {
    return this.value(control) > 0.5;
  }

  /** Pressed since the step before this one (true for exactly one step per press). */
  pressed(control: string): boolean {
    return this.stepEdges.has(control);
  }

  vector(control: string): Vec2 {
    return this.vectors.get(control) ?? { x: 0, y: 0 };
  }

  /** Read and clear an accumulated delta. */
  takeDelta(control: string): Vec2 {
    const d = this.deltas.get(control);
    if (!d) return { x: 0, y: 0 };
    this.deltas.delete(control);
    return d;
  }

  // ── step boundaries (the runtime calls these around every sim step) ──────────────────────────────

  beginStep(): void {
    for (const c of this.pending) this.stepEdges.add(c);
    this.pending.clear();
  }

  endStep(): void {
    this.stepEdges.clear();
  }

  // ── gamepads (polled once per frame, in the input phase) ─────────────────────────────────────────

  /** Merge every connected pad into the Pad* controls (the strongest value wins). */
  pollPads(pads: readonly (PadLike | null | undefined)[], dead = PAD_DEAD_ZONE): void {
    const next = new Map<string, number>();
    let left: Vec2 = { x: 0, y: 0 };
    let right: Vec2 = { x: 0, y: 0 };
    for (const p of pads) {
      if (!p || p.connected === false) continue;
      p.buttons.forEach((b, i) => {
        const v = b.pressed && b.value === 0 ? 1 : b.value;
        if (v > (next.get(`Pad${i}`) ?? 0)) next.set(`Pad${i}`, v);
      });
      p.axes.forEach((a, i) => {
        const v = Math.abs(a) <= dead ? 0 : (Math.abs(a) - dead) / (1 - dead);
        const k = `PadAxis${i}${a < 0 ? "-" : "+"}`;
        if (v > (next.get(k) ?? 0)) next.set(k, v);
      });
      // standard mapping: axes 0/1 left stick, 2/3 right stick, +y is down on the stick (so forward is -y)
      const l = radialDeadZone(p.axes[0] ?? 0, -(p.axes[1] ?? 0), dead);
      const r = radialDeadZone(p.axes[2] ?? 0, -(p.axes[3] ?? 0), dead);
      if (Math.hypot(l.x, l.y) > Math.hypot(left.x, left.y)) left = l;
      if (Math.hypot(r.x, r.y) > Math.hypot(right.x, right.y)) right = r;
    }
    for (const [k, prev] of this.padPrev) if (!next.has(k) && prev > 0) this.values.delete(k);
    const edges: string[] = [];
    for (const [k, v] of next) {
      if (v > 0.5 && !((this.padPrev.get(k) ?? 0) > 0.5)) edges.push(k);
      if (v > 0) this.values.set(k, v);
      else this.values.delete(k);
    }
    this.padPrev = next;
    for (const k of edges) this.edge(k);
    // a stick counts as the pad being used when it crosses half deflection (not on drift near the dead zone)
    for (const [name, v] of [["PadLeft", left], ["PadRight", right]] as const) {
      const m = Math.hypot(v.x, v.y);
      if (m >= 0.5 && this.padVec[name] < 0.5) this.noteDevice("gamepad");
      this.padVec[name] = m;
      if (v.x === 0 && v.y === 0) this.vectors.delete(name);
      else this.vectors.set(name, v);
    }
  }

  /** The sim-time input state a save must carry: injected holds (their end times are sim time, so they resume
   *  exactly with a restored clock) and presses latched for the next step. Live device state (keys physically
   *  down, pointer deltas, pads) is the player's hands, not the save's, and is left alone. JSON-safe. */
  snapshot(): ControlsState {
    return { holds: [...this.holds.entries()], pending: [...this.pending] };
  }

  restore(s: ControlsState): void {
    this.holds = new Map(s.holds);
    this.pending = new Set(s.pending);
    this.stepEdges.clear();
  }

  /** What is held, injected and latched (for a harness's input view). */
  describe(): { held: string[]; holds: Record<string, number>; pending: string[]; vectors: Record<string, Vec2> } {
    const t = this.now();
    return {
      held: [...this.values.entries()].filter(([, v]) => v > 0.5).map(([k]) => k),
      holds: Object.fromEntries([...this.holds.entries()].filter(([, u]) => u > t).map(([k, u]) => [k, Math.round((u - t) * 1000) / 1000])),
      pending: [...this.pending],
      vectors: Object.fromEntries(this.vectors),
    };
  }
}

/** The DOM events Controls listens to (a Window and an element; structural, so tests can fake them). */
export interface EventTargetLike {
  // (the listener takes `any` so a real Window or element fits: their listeners are typed per event name)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListener(type: string, fn: (e: any) => void, opts?: unknown): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeEventListener(type: string, fn: (e: any) => void, opts?: unknown): void;
}

/** Options for `attachDom`. */
export interface AttachDomOptions {
  /** What a touch pointer (pointerType "touch") writes: "mouse" (the default: a tap is Mouse0 and a drag is
   *  PointerDrag) or "ignore" (only the device switch is noted; an on-screen touch layer writes the touch controls). */
  touchPointers?: "mouse" | "ignore";
  /** Whether the pointer is locked now: locked motion goes to "PointerLocked" and not to "PointerDrag".
   *  Default: `document.pointerLockElement` is set, when there is a document. */
  locked?: () => boolean;
  /** Where page-hidden events come from (visibilitychange): everything is released when the page is hidden.
   *  Default the global document, when there is one. */
  doc?: EventTargetLike & { visibilityState?: string };
}

/**
 * Wire keyboard (on `win`) and pointer (on `el`) events into `controls`. Pointer events cover mouse, pen and touch;
 * with `touchPointers: "ignore"` a touch only switches the device (a touch layer writes its own controls). Motion
 * goes to "PointerMove" always, "PointerDrag" while a button is down and the pointer isn't locked, and
 * "PointerLocked" while it is. Everything held is released on blur, on pagehide and when the page is hidden.
 * Returns the detach function.
 */
export function attachDom(
  controls: Controls,
  win: EventTargetLike,
  el: EventTargetLike & { setPointerCapture?(id: number): void },
  o: AttachDomOptions = {},
): () => void {
  const doc = o.doc ?? (typeof document !== "undefined" ? (document as unknown as EventTargetLike & { visibilityState?: string }) : undefined);
  const locked = o.locked ?? (() => typeof document !== "undefined" && document.pointerLockElement != null);
  const ignoreTouch = o.touchPointers === "ignore";
  const kd = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    controls.press(e.code);
  };
  const ku = (e: KeyboardEvent): void => controls.release(e.code);
  const blur = (): void => controls.releaseAll();
  const hidden = (): void => {
    if (doc?.visibilityState === "hidden") controls.releaseAll();
  };
  let buttons = 0;
  const pd = (e: PointerEvent): void => {
    if (ignoreTouch && e.pointerType === "touch") return controls.noteDevice("touch");
    buttons |= 1 << e.button;
    controls.press(`Mouse${e.button}`);
    try {
      el.setPointerCapture?.(e.pointerId);
    } catch {
      // (synthetic events have no active pointer to capture)
    }
  };
  const pu = (e: PointerEvent): void => {
    if (ignoreTouch && e.pointerType === "touch") return;
    buttons &= ~(1 << e.button);
    controls.release(`Mouse${e.button}`);
  };
  const pm = (e: PointerEvent): void => {
    if (ignoreTouch && e.pointerType === "touch") return;
    controls.addDelta("PointerMove", e.movementX, e.movementY);
    if (locked()) controls.addDelta("PointerLocked", e.movementX, e.movementY);
    else if (buttons) controls.addDelta("PointerDrag", e.movementX, e.movementY);
  };
  const wh = (e: WheelEvent): void => controls.addDelta("Wheel", e.deltaX, e.deltaY);
  win.addEventListener("keydown", kd);
  win.addEventListener("keyup", ku);
  win.addEventListener("blur", blur);
  win.addEventListener("pagehide", blur);
  doc?.addEventListener("visibilitychange", hidden);
  el.addEventListener("pointerdown", pd);
  el.addEventListener("pointerup", pu);
  el.addEventListener("pointercancel", pu);
  el.addEventListener("pointermove", pm);
  el.addEventListener("wheel", wh, { passive: true });
  return () => {
    win.removeEventListener("keydown", kd);
    win.removeEventListener("keyup", ku);
    win.removeEventListener("blur", blur);
    win.removeEventListener("pagehide", blur);
    doc?.removeEventListener("visibilitychange", hidden);
    el.removeEventListener("pointerdown", pd);
    el.removeEventListener("pointerup", pu);
    el.removeEventListener("pointercancel", pu);
    el.removeEventListener("pointermove", pm);
    el.removeEventListener("wheel", wh);
  };
}
